/**
 * Booking Service
 * 
 * Receipt API Endpoints:
 * ========================
 * GET  /api/v1/agency/bookings/{bookingId}/receipt  - Download receipt PDF
 * POST /api/v1/agency/bookings/{bookingId}/receipt  - Upload receipt PDF
 * 
 * CORS Configuration:
 * - Backend CORS allows: app.karia.tn, admin.karia.tn, api.cararabia.app
 * - Frontend uses VITE_API_URL environment variable
 * - Credentials included in all requests
 * 
 * If CORS errors occur:
 * 1. Check backend/server.js corsOrigins configuration
 * 2. Verify Access-Control-Allow-Origin header is present
 * 3. Ensure domain is in CORS whitelist
 */

import apiClient from './api';
import {
  Booking,
  BookingFormData,
  BookingListFilters,
  BookingListResponse,
  BookingResponse,
  PricingRequest,
  PricingCalculation,
  AvailabilityCheck,
  DashboardStats,
  AnalyticsData
} from '../types/booking';

const BASE_URL = '/agency/bookings';
let dashboardStatsPromise: Promise<DashboardStats> | null = null;
let dashboardStatsCache: DashboardStats | null = null;
let dashboardStatsLastFetchedAt = 0;
const DASHBOARD_STATS_MIN_REFETCH_MS = 10_000;

const getExplicitAuthHeaders = () => {
  if (typeof window === 'undefined') return {};

  const userType = localStorage.getItem('user_type');
  const employeeToken = localStorage.getItem('employee_token');
  const agencyToken = localStorage.getItem('agency_token');
  const token = userType === 'employee'
    ? (employeeToken || agencyToken)
    : (agencyToken || employeeToken);

  return token
    ? { Authorization: `Bearer ${token}` }
    : {};
};

const emitRevenueRefresh = (bookingId?: string) => {
  if (typeof window === 'undefined') return;

  try {
    window.dispatchEvent(new CustomEvent('revenue:payment-recorded', {
      detail: {
        bookingId,
        at: new Date().toISOString(),
      },
    }));
  } catch {
    // Non-browser or unsupported event environments can safely ignore this.
  }
};

const invalidateDashboardStatsCache = () => {
  dashboardStatsPromise = null;
  dashboardStatsCache = null;
  dashboardStatsLastFetchedAt = 0;
};

export const bookingService = {
  async paymentHistory(id: string): Promise<{ payments: Booking['payments']; needsReconciliation: boolean }> {
    const response = await apiClient.get(`${BASE_URL}/${id}/payments`);
    return response.data.data;
  },
  async correctPayment(id: string, paymentId: string, fields: { expectedVersion: number; amount?: number; method?: string; paidAt?: string; transactionId?: string; notes?: string }, remove = false): Promise<Booking> {
    const response = remove ? await apiClient.delete(`${BASE_URL}/${id}/payments/${paymentId}`, { data: fields }) : await apiClient.patch(`${BASE_URL}/${id}/payments/${paymentId}`, fields);
    invalidateDashboardStatsCache(); emitRevenueRefresh(id);
    return response.data.data;
  },
  async deletionPreview(id: string): Promise<{ payments: number; contracts: number; receipts: number; version: number; activeRental: boolean }> {
    return (await apiClient.get(`${BASE_URL}/${id}/deletion-preview`, { headers: { 'Cache-Control': 'no-store' } })).data.data;
  },
  async deleteBooking(id: string, expectedVersion: number): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`, { data: { expectedVersion } });
    invalidateDashboardStatsCache(); emitRevenueRefresh(id);
  },

  refreshFinancialViews(bookingId?: string) {
    invalidateDashboardStatsCache();
    emitRevenueRefresh(bookingId);
  },
  /**
   * Check vehicle availability
   */
  async checkAvailability(
    vehicleId: string,
    startDate: string,
    endDate: string,
    excludeBookingId?: string
  ): Promise<AvailabilityCheck> {
    const response = await apiClient.get<{
      success: boolean;
      data: AvailabilityCheck;
    }>(`${BASE_URL}/availability`, {
      params: { vehicleId, startDate, endDate, ...(excludeBookingId ? { excludeBookingId } : {}) }
    });
    return response.data.data;
  },

  /**
   * Calculate pricing
   */
  async calculatePricing(data: PricingRequest): Promise<PricingCalculation> {
    const response = await apiClient.post<{
      success: boolean;
      data: PricingCalculation;
    }>(`${BASE_URL}/calculate-pricing`, data);
    return response.data.data;
  },

  /**
   * Create new booking
   */
  async createBooking(data: BookingFormData): Promise<Booking> {
    const response = await apiClient.post<BookingResponse>(BASE_URL, data);
    return response.data.data;
  },

  /**
   * Get booking by ID
   */
  async getBooking(bookingId: string, options?: { fresh?: boolean }): Promise<Booking> {
    const response = options?.fresh
      ? await apiClient.get<BookingResponse>(`${BASE_URL}/${bookingId}`, { headers: { 'Cache-Control': 'no-store' } })
      : await apiClient.get<BookingResponse>(`${BASE_URL}/${bookingId}`);
    const booking = response.data.data as Booking & { __v?: number };
    return { ...booking, version: booking.version ?? booking.__v ?? 0 };
  },

  /**
   * List bookings with filters
   */
  async listInvoices(filters?: BookingListFilters): Promise<BookingListResponse['data']> {
    const response = await apiClient.get<BookingListResponse>('/agency/invoices', { params: filters });
    if (!response.data.success || !Array.isArray(response.data.data?.bookings)) throw new Error(response.data.message || 'Invalid invoice list response');
    return response.data.data;
  },

  async listBookings(filters?: BookingListFilters): Promise<BookingListResponse['data']> {
    const response = await apiClient.get<BookingListResponse>(BASE_URL, {
      params: filters
    });
    if (!response.data.success || !Array.isArray(response.data.data?.bookings)) {
      throw new Error(response.data.message || 'Invalid booking list response');
    }
    return response.data.data;
  },

  /**
   * Update booking
   */
  async updateBooking(bookingId: string, data: Partial<BookingFormData>): Promise<Booking> {
    const sanitized = { ...data };
    if (sanitized.pricing) {
      const allowed = new Set(['baseRate', 'priceTimesDaysAmount', 'baseVatAmount', 'baseVatPerDay', 'baseVatIncludedInPrice', 'vatAmount', 'vatRate', 'vatIncludedInPrice', 'cautionnement', 'deposit', 'discountPercentage', 'subtotalAfterDiscount', 'applyVatToExtras']);
      sanitized.pricing = Object.fromEntries(Object.entries(sanitized.pricing).filter(([key]) => allowed.has(key))) as typeof sanitized.pricing;
    }
    const response = await apiClient.put<BookingResponse>(`${BASE_URL}/${bookingId}`, sanitized);
    invalidateDashboardStatsCache();
    return response.data.data;
  },

  /**
   * Confirm booking (after payment)
   */
  async confirmBooking(
    bookingId: string,
    paymentData: {
      amount: number;
      method: string;
      transactionId?: string;
      paymentType?: string;
      notes?: string;
    }
  ): Promise<{ booking: Booking; payment: any }> {
    const response = await apiClient.patch<{
      success: boolean;
      data: { booking: Booking; payment: any };
    }>(`${BASE_URL}/${bookingId}/confirm`, paymentData);
    emitRevenueRefresh(bookingId);
    return response.data.data;
  },

  /**
   * Add payment to booking
   */
  async addPayment(
    bookingId: string,
    paymentData: {
      amount: number;
      method: string;
      transactionId?: string;
      notes?: string;
      balanceCollectionTime?: 'handover' | 'return';
      additionalCharges?: number;
    }
  ): Promise<{ booking: Booking; payment: any }> {
    const response = await apiClient.post<{
      success: boolean;
      data: { booking: Booking; payment: any };
    }>(`${BASE_URL}/${bookingId}/payments`, paymentData);
    emitRevenueRefresh(bookingId);
    return response.data.data;
  },

  /**
   * Record refundable cautionnement separately from rental revenue/payment.
   */
  async recordCautionnement(
    bookingId: string,
    paymentData: {
      amount: number;
      method: string;
      transactionId?: string;
      notes?: string;
    }
  ): Promise<{ booking: Booking; payment: any }> {
    const response = await apiClient.post<{
      success: boolean;
      data: { booking: Booking; payment: any };
    }>(`${BASE_URL}/${bookingId}/cautionnement`, paymentData);
    emitRevenueRefresh(bookingId);
    return response.data.data;
  },

  /**
   * Vehicle handover
   */
  async handoverVehicle(
    bookingId: string,
    data: {
      fuelLevel?: number;
      mileage?: number;
      condition?: string;
      photos?: string[];
      notes?: string;
      handedOverBy?: string;
      contractReference?: string;
      actualHandoverTime?: string;
      scheduledHandoverTime?: string;
    }
  ): Promise<Booking> {
    const response = await apiClient.patch<BookingResponse>(
      `${BASE_URL}/${bookingId}/handover`,
      data
    );
    invalidateDashboardStatsCache();
    return response.data.data;
  },

  /**
   * Process vehicle return
   */
  async returnVehicle(
    bookingId: string,
    data: {
      fuelLevel: number;
      mileage: number;
      condition: string;
      damages?: Array<{
        description: string;
        severity: string;
        estimatedCost: number;
        photos: string[];
      }>;
      photos?: string[];
      notes?: string;
      receivedBy: string;
      lateFee?: number;
      damageCharge?: number;
      fuelCharge?: number;
      cleaningFee?: number;
      additionalChargesPaid?: boolean;
      paymentMethod?: string;
      additionalChargesPaymentDetails?: {
        transactionId?: string;
        notes?: string;
      };
      balancePayment?: {
        amount: number;
        method: string;
        transactionId?: string;
        notes?: string;
      };
    }
  ): Promise<Booking> {
    const response = await apiClient.patch<BookingResponse>(
      `${BASE_URL}/${bookingId}/return`,
      data
    );
    invalidateDashboardStatsCache();
    return response.data.data;
  },

  /**
   * Cancel booking
   */
  async cancelBooking(bookingId: string, reason: string): Promise<Booking> {
    const response = await apiClient.patch<BookingResponse>(
      `${BASE_URL}/${bookingId}/cancel`,
      { reason }
    );
    invalidateDashboardStatsCache();
    return response.data.data;
  },

  /**
   * Extend booking rental period
   */
  async extendBooking(
    bookingId: string,
    data: {
      newEndDate: string;
      reason: string;
      additionalCost: number;
      payment?: {
        amount: number;
        method: string;
        transactionId?: string;
        notes?: string;
        balanceCollectionTime?: 'handover' | 'return';
      };
    }
  ): Promise<Booking> {
    const response = await apiClient.patch<BookingResponse>(
      `${BASE_URL}/${bookingId}/extend`,
      data
    );
    return response.data.data;
  },

  /**
   * Get vehicles available as alternatives during an extension period
   */
  async getExtensionVehicles(
    excludeVehicleId: string,
    startDate: string,
    endDate: string
  ): Promise<any[]> {
    const response = await apiClient.get<{ success: boolean; data: any[] }>(
      `${BASE_URL}/extension-vehicles`,
      { params: { excludeVehicleId, startDate, endDate } }
    );
    return response.data.data;
  },

  /**
   * Get dashboard stats
   */
  async getDashboardStats(): Promise<DashboardStats> {
    const now = Date.now();

    if (dashboardStatsPromise) {
      return dashboardStatsPromise;
    }

    if (dashboardStatsCache && now - dashboardStatsLastFetchedAt < DASHBOARD_STATS_MIN_REFETCH_MS) {
      return dashboardStatsCache;
    }

    dashboardStatsPromise = (async () => {
      try {
        const response = await apiClient.get<{
          success: boolean;
          data: DashboardStats;
        }>(`${BASE_URL}/dashboard`);
        dashboardStatsCache = response.data.data;
        dashboardStatsLastFetchedAt = Date.now();
        return response.data.data;
      } finally {
        dashboardStatsPromise = null;
      }
    })();

    return dashboardStatsPromise;
  },

  /**
   * Get analytics
   */
  async getAnalytics(startDate: string, endDate: string): Promise<AnalyticsData> {
    const response = await apiClient.get<{
      success: boolean;
      data: AnalyticsData;
    }>(`${BASE_URL}/analytics`, {
      params: { startDate, endDate }
    });
    return response.data.data;
  },

  /**
   * Record a receipt print event
   */
  async recordPrint(
    bookingId: string,
    data: {
      printedBy?: string;
      receiptType?: string;
      notes?: string;
    }
  ): Promise<any> {
    const response = await apiClient.post<{
      success: boolean;
      data: any;
    }>(`/agency/invoices/${bookingId}/print`, data);
    return response.data.data;
  },

  /**
   * Upload booking receipt PDF
   */
  async uploadReceipt(
    bookingId: string,
    data: {
      pdfBase64: string;
      filename?: string;
    }
  ): Promise<any> {
    const response = await apiClient.post<{
      success: boolean;
      data: any;
    }>(`/agency/invoices/${bookingId}/receipt`, data, {
      skipAuthRedirect: true,
      headers: {
        ...getExplicitAuthHeaders(),
      },
    } as any);
    return response.data.data;
  },

  /**
   * Fetch booking receipt PDF as blob
   */
  async getReceipt(bookingId: string): Promise<Blob> {
    const response = await apiClient.get(`/agency/invoices/${bookingId}/receipt`, {
      responseType: 'blob',
      headers: {
        ...getExplicitAuthHeaders(),
      },
    });
    return response.data;
  },

  /**
   * Fetch agency-specific booking settings
   */
  async getBookingSettings(): Promise<any> {
    const response = await apiClient.get('/agency-settings/booking-settings');
    return response.data;
  },

  /**
   * Upload customer photos for a booking
   */
  async uploadCustomerPhotos(bookingId: string, photos: {
    clientPhoto?: File;
    licensePhoto?: File;
    idPhoto?: File;
  }): Promise<any> {
    const formData = new FormData();
    if (photos.clientPhoto) formData.append('clientPhoto', photos.clientPhoto);
    if (photos.licensePhoto) formData.append('licensePhoto', photos.licensePhoto);
    if (photos.idPhoto) formData.append('idPhoto', photos.idPhoto);

    const response = await apiClient.patch(`${BASE_URL}/${bookingId}/photos`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

export default bookingService;
